/**
 * AnalystBubble — persistent floating analyst shortcut.
 *
 * Sits above the ChatButton (bottom-right). Always visible for authenticated
 * users when the drawer is closed. Click opens the Expert Chat drawer.
 * Mic icon opens it in voice mode. Hidden on /branch/ routes via GlobalChat
 * in App.tsx — no interference with banker meeting sessions.
 */

import React, { useCallback } from 'react';
import { Mic } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useExpertChat } from '../../contexts/ExpertChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { expertApi } from '../../services/expertApi';
import BankerAvatar from '../avatar/BankerAvatar';

const AnalystBubble: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { openDrawer, setVoiceModeEnabled, isDrawerOpen } = useExpertChat();

  const { data: bankerPrefs } = useQuery({
    queryKey: ['banker-preferences'],
    queryFn: () => expertApi.getBankerPreferences(),
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated,
  });

  const analystName = bankerPrefs?.banker_analyst_name ?? 'Market Analyst';

  const handleClick = useCallback(() => {
    openDrawer();
  }, [openDrawer]);

  const handleMic = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setVoiceModeEnabled(true);
      openDrawer();
    },
    [openDrawer, setVoiceModeEnabled],
  );

  // Hidden when not authenticated or when the drawer is already open
  if (!isAuthenticated || isDrawerOpen) return null;

  return (
    <div
      className="fixed bottom-24 right-6 z-40 flex flex-col items-center gap-1 pointer-events-none"
      aria-label={`${analystName} standby`}
    >
      {/* Name label */}
      <span className="text-[10px] text-slate-400 font-medium tracking-wide pointer-events-none select-none">
        {analystName}
      </span>

      {/* Main bubble */}
      <div className="relative pointer-events-auto">
        {/* Idle pulse ring */}
        <span className="absolute inset-0 rounded-full bg-blue-500/20 animate-ping" />

        <button
          onClick={handleClick}
          title={`Open ${analystName}`}
          className="
            relative w-14 h-14 rounded-full
            bg-slate-800 border-2 border-blue-500/60
            shadow-lg shadow-blue-500/20
            flex items-center justify-center
            overflow-hidden
            transition-transform duration-200 hover:scale-105
          "
        >
          <div style={{ transform: 'scale(0.85) translateY(6px)', marginTop: '-4px' }}>
            <BankerAvatar state="idle" persona="analyst" size="sm" />
          </div>
        </button>

        {/* Mic button — bottom-left of bubble */}
        <button
          onClick={handleMic}
          title="Open in voice mode"
          className="
            absolute -bottom-1 -left-1
            w-6 h-6 rounded-full
            bg-teal-500 hover:bg-teal-400
            flex items-center justify-center
            shadow-md
            transition-colors duration-150
          "
        >
          <Mic size={12} className="text-white" />
        </button>
      </div>
    </div>
  );
};

export default AnalystBubble;
